import {
  faFilter,
  faMagnifyingGlass,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FormEvent, useEffect, useState } from "react";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { GuideListItem } from "../../shared/contracts";
import { api, ApiClientError, isAbortError } from "../api";
import { useAuth } from "../auth";
import { DecisionCard } from "../components/DecisionCard";

interface GuideResponse {
  items: GuideListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export function LibraryPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeQuery = params.get("q") ?? "";
  const [query, setQuery] = useState(activeQuery);
  const [data, setData] = useState<GuideResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const scope = params.get("scope") === "mine" && user ? "mine" : "public";
  const canCreateGuide = Boolean(
    user &&
    (user.isSystemAdmin ||
      user.account.role === "owner" ||
      user.account.role === "manager"),
  );
  const requestedPage = Number(params.get("page") ?? 1);
  const page =
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const hasFilters = Boolean(
    activeQuery ||
    params.get("hearingType") ||
    (scope === "mine" && params.get("status")) ||
    params.get("from") ||
    params.get("to"),
  );
  useEffect(() => {
    if (requestedPage === page) return;
    const next = new URLSearchParams(params);
    next.set("page", String(page));
    setParams(next, { replace: true });
  }, [page, params, requestedPage, setParams]);
  useEffect(() => {
    setData(null);
  }, [scope]);
  useEffect(() => {
    const controller = new AbortController();
    const search = new URLSearchParams(params);
    search.set("scope", scope);
    search.set("page", String(page));
    search.set("pageSize", "25");
    setError("");
    setLoading(true);
    void api<GuideResponse>(`/api/guides?${search.toString()}`, {
      signal: controller.signal,
    })
      .then(setData)
      .catch((caught: unknown) => {
        if (isAbortError(caught)) return;
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : "Unable to load guides.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, params, reloadKey, scope]);
  useEffect(() => {
    setQuery(activeQuery);
  }, [activeQuery]);
  useEffect(() => {
    if (!data || data.page !== page) return;
    const lastPage = Math.max(1, Math.ceil(data.total / data.pageSize));
    if (page <= lastPage) return;
    const next = new URLSearchParams(params);
    next.set("page", String(lastPage));
    setParams(next, { replace: true });
  }, [data, page, params, setParams]);
  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (query.trim()) next.set("q", query.trim());
    else next.delete("q");
    next.set("page", "1");
    setParams(next);
  };
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };
  const updateScope = (nextScope: "public" | "mine") => {
    const next = new URLSearchParams(params);
    next.set("scope", nextScope);
    next.set("page", "1");
    if (nextScope === "public") next.delete("status");
    setParams(next);
  };
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const key of ["q", "hearingType", "status", "from", "to"])
      next.delete(key);
    next.set("page", "1");
    setQuery("");
    setParams(next);
  };
  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(nextPage));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <div className="page-section library-page">
      <div className="container">
        <div className="page-heading library-heading">
          <div>
            <p className="kicker">Voting position guides</p>
            <h1>{scope === "mine" ? "Your guides" : "Published guides"}</h1>
            <p>
              {scope === "mine"
                ? "Draft, review, and publish your organization’s positions."
                : "Search published organizational positions and voting guides."}
            </p>
          </div>
          {canCreateGuide ? (
            <Link className="button" to="/guides/new">
              <FontAwesomeIcon icon={faPlus} /> New guide
            </Link>
          ) : null}
        </div>
        {user ? (
          <nav className="section-tabs" aria-label="Guide scope">
            <button
              className={scope === "public" ? "active" : ""}
              onClick={() => updateScope("public")}
            >
              Public
            </button>
            <button
              className={scope === "mine" ? "active" : ""}
              onClick={() => updateScope("mine")}
            >
              Your guides
            </button>
          </nav>
        ) : null}
        <div className="library-tools">
          <form className="search-box" onSubmit={submitSearch}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              aria-label="Search guides"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search titles and descriptions"
            />
            <button className="button button-small">Search</button>
          </form>
          <fieldset className="filters">
            <legend>
              <FontAwesomeIcon icon={faFilter} /> Filters
            </legend>
            {scope === "mine" ? (
              <label className="filter-field">
                <span>Status</span>
                <select
                  value={params.get("status") ?? ""}
                  onChange={(event) => update("status", event.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
            ) : null}
            <label className="filter-field">
              <span>Hearing structure</span>
              <select
                value={params.get("hearingType") ?? ""}
                onChange={(event) => update("hearingType", event.target.value)}
              >
                <option value="">All hearing structures</option>
                <option value="simple">Single round</option>
                <option value="two_vote">Two rounds</option>
                <option value="three_vote">Three rounds</option>
                <option value="four_vote">Four rounds</option>
                <option value="flexible">Flexible rounds</option>
              </select>
            </label>
            <label className="filter-field">
              <span>From</span>
              <input
                type="date"
                value={params.get("from") ?? ""}
                onChange={(event) => update("from", event.target.value)}
              />
            </label>
            <label className="filter-field">
              <span>To</span>
              <input
                type="date"
                value={params.get("to") ?? ""}
                onChange={(event) => update("to", event.target.value)}
              />
            </label>
            {hasFilters ? (
              <button
                className="button button-ghost button-small filter-clear"
                type="button"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
          </fieldset>
        </div>
        <p className="library-count" aria-live="polite">
          {data
            ? `${String(data.total)} guide${data.total === 1 ? "" : "s"}${loading ? " · Updating" : ""}`
            : error
              ? "Guides unavailable"
              : "Loading guides"}
        </p>
        {error ? (
          <div className="alert alert-error" role="alert">
            <p>{error}</p>
            <button
              className="button button-small button-secondary"
              type="button"
              onClick={() => setReloadKey((current) => current + 1)}
            >
              Try again
            </button>
          </div>
        ) : null}
        {!data && !error ? (
          <div className="loading-block" role="status">
            Loading guides…
          </div>
        ) : null}
        {data?.items.length === 0 ? (
          <div className="empty-state">
            <h2>
              {scope === "mine" ? "No guides yet" : "No published guides"}
            </h2>
            <p>
              {hasFilters
                ? "Adjust the search or filters to see more results."
                : scope === "mine"
                  ? "Create your first guide to begin reviewing positions."
                  : "Published organizational guides will appear here."}
            </p>
            {scope === "mine" && !hasFilters && canCreateGuide ? (
              <Link className="button" to="/guides/new">
                <FontAwesomeIcon icon={faPlus} /> New guide
              </Link>
            ) : null}
          </div>
        ) : null}
        <div
          className={`decision-list${loading && data ? " is-refreshing" : ""}`}
          aria-busy={loading && Boolean(data)}
        >
          {data?.items.map((guide) => (
            <DecisionCard key={guide.id} decision={guide} />
          ))}
        </div>
        {data && data.total > data.pageSize ? (
          <nav className="pagination" aria-label="Guide pages">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {Math.ceil(data.total / data.pageSize)}
            </span>
            <button
              disabled={page >= Math.ceil(data.total / data.pageSize)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
